<script setup lang="ts">
import type { ContentFormWriter } from '~/models/Content/ContentFormWriter'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { faImage, faTrash } from '@fortawesome/pro-regular-svg-icons'
import { KsIcon } from '@aschehoug/kloss-2'
import { KsButton, KsProgress } from '@aschehoug/kloss'
import useAuthStore from '~/stores/auth'
import { PendoFeature } from '~/models/Pendo'
import useImageUpload from '~/composables/useImageUpload'
import AuTooltip from '~/components/utils/AuTooltip.vue'

const { item } = defineProps<{ item: ContentFormWriter }>()

const images = defineModel<string[]>('images', { default: [] })
const currentImage = defineModel<string>('currentImage')

const { t } = useI18n()
const { isTeacher } = storeToRefs(useAuthStore())
const { uploadImage, progress, imageSrc, reset } = useImageUpload()

const fileInput = ref<HTMLInputElement | null>(null)

const paragraphText = computed(() => {
  const hasImageUpload = item.imageUpload
  const hasImages = item.imagesOnCanvas.destinationContentIds.length > 0

  if (isTeacher.value && item.assignment)
    return t('formWriter.imageTeacher')
  if (uploadImage.isPending.value)
    return t('formWriter.uploadingImage')
  if (hasImageUpload && hasImages)
    return t('formWriter.chooseOrUploadImage')
  if (!hasImageUpload && hasImages)
    return t('formWriter.chooseImage')
  if (hasImageUpload && !hasImages)
    return t('formWriter.uploadImage')

  return ''
})

const clearImageAndFile = () => {
  reset()
  currentImage.value = ''
  if (fileInput.value) {
    fileInput.value.value = ''
  }
}

const onChange = async (event: Event) => {
  await uploadImage.mutateAsync(event)
  if (uploadImage.isSuccess.value && imageSrc.value) {
    currentImage.value = imageSrc.value
    images.value = [imageSrc.value, ...images.value]
  }
}
</script>

<template>
  <div class="mb-4">
    <div v-if="!currentImage" class="flex w-full flex-col items-center justify-center rounded-lg border-2 border-dotted border-[--theme-50] p-16 font-bold">
      <KsIcon :icon="faImage" class="size-12" />
      <p class="mb-4 mt-2 max-w-xs text-center font-medium" v-text="paragraphText" />
      <template v-if="item.imageUpload && !(isTeacher && item.assignment)">
        <label
          v-if="!uploadImage.isPending.value"
          class="flex"
          for="file-upload"
        >
          <span class="sr-only" v-text="t('formWriter.uploadImage')" />
          <input
            id="file-upload"
            ref="fileInput"
            type="file"
            accept="image/*"
            :data-pendo="PendoFeature.FormWriter.UploadImage"
            class="cursor-pointer justify-between rounded-lg bg-[--theme-5] p-2 text-center text-sm ring-2 ring-[--theme-50] transition file:mr-4 file:rounded-full file:border-0 file:bg-[--theme-50] file:px-4 file:py-2 file:text-[--theme-5] hover:bg-[--theme-10] hover:ring hover:file:bg-[--theme-40] focus-visible:ring"
            @change="onChange"
          >
        </label>
        <KsProgress
          v-else
          :max="100"
          :values="[progress]"
          :style="{
            '--ks-primary': 'var(--theme-50)',
            '--ks-input': 'var(--theme-5)',
          }"
        />
      </template>
    </div>
    <div v-else class="relative">
      <img
        :src="currentImage"
        class="aspect-[3/2] size-full rounded-lg object-cover"
        alt=""
      >
      <AuTooltip
        v-if="!(isTeacher && item.assignment)"
        :content="t('formWriter.deleteImage')"
      >
        <KsButton
          shape="square"
          variant="secondary"
          class="absolute bottom-4 right-4 print:!hidden"
          :icon-left="faTrash"
          @click="clearImageAndFile"
        />
      </AuTooltip>
    </div>
  </div>
</template>
